import { Injectable } from '@angular/core';
import { CoachBranding } from '@cf-shared/data-model/coach';
import { deriveBrandPalette, BrandPalette } from '@cf-shared/color-math';
import { LoggingService } from 'services/logging.service';

/**
 * localStorage key holding the minimal branding the warm-boot splash needs
 * (logo URL, accent hex, brand name). Written every time applyBranding() runs
 * with a valid accent, so a later launch can theme the in-app splash instantly
 * — before the user DB has loaded.
 *
 * IMPORTANT: this key is user-scoped and must be wiped on sign-out. It is
 * deliberately NOT on the preserve allow-list in local-storage-hygiene.ts, so
 * clearUserScopedLocalStorage() (called from the sign-out path) wipes it by
 * default. The key name also does not match any PRESERVED_LOCALSTORAGE_PREFIXES
 * entry. Do not add it to that list. See design-docs/coach-white-labeling.md.
 */
export const COACH_BRANDING_CACHE_KEY = 'coachBrandingCache';

/** The minimal shape cached for the warm-boot splash. */
export interface CachedCoachBranding {
  logoUrl?: string;
  accentColorHex?: string;
  brandName?: string;
}

// The set of `-override` CSS custom properties this service writes on
// document.documentElement. Each maps to one field of the derived palette.
// Keeping them in one list means apply and clear can never drift apart.
const OVERRIDE_VARS: { cssVar: string; field: keyof BrandPalette }[] = [
  { cssVar: '--cs-brand-primary-override', field: 'primary' },
  { cssVar: '--cs-brand-primary-hover-override', field: 'hover' },
  { cssVar: '--cs-brand-primary-active-override', field: 'active' },
  { cssVar: '--cs-brand-tint-override', field: 'tint' },
  { cssVar: '--cs-brand-on-accent-override', field: 'onAccent' },
  { cssVar: '--cs-brand-link-override', field: 'link' },
  { cssVar: '--cs-brand-link-hover-override', field: 'linkHover' },
];

/**
 * Runtime theming for coach white-labeling (#3707). Turns one coach-picked
 * accent hex into the full `--cs-brand-*` CSS variable family on the document
 * root, so the bounded set of recolor-target components (primary buttons,
 * links, input focus, selected states) follow the coach's brand. The derived
 * shade family and contrast-safe on-accent text are computed here at runtime
 * from the single stored accent hex — nothing derived is persisted.
 *
 * This is the only writer of the `--cs-brand-*-override` variables. When it
 * sets nothing, the MH defaults declared in _colors.scss show — a clean on/off
 * switch. Mirrors the runtime-CSS-var pattern in cs-coach-banner.ts.
 */
@Injectable({ providedIn: 'root' })
export class CoachBrandingThemeService {
  constructor(private logging: LoggingService) {}

  /**
   * Apply a coach's branding. If `branding` has a valid `accentColorHex`,
   * derive the palette, set each `--cs-brand-*-override`, and (by default) cache
   * the splash fields. If the branding is missing or the accent is invalid, fall
   * back to the MH default by clearing every override (so switching coaches, or
   * losing a coach, reverts cleanly).
   *
   * Pass `{ cache: false }` to theme the live DOM WITHOUT writing the warm-boot
   * splash cache — used on the pre-claim login screen, where the viewer is not
   * (yet) this coach's client, so the splash must not be themed on a later boot.
   *
   * Returns true when a brand was applied, false when it fell back to MH — the
   * caller uses this to drive the `coach-branded` host class on cs-app.
   */
  applyBranding(branding: CoachBranding | undefined | null, opts?: { cache?: boolean }): boolean {
    const palette = deriveBrandPalette(branding?.accentColorHex);
    if (!palette) {
      // no branding, or an unparseable accent — revert to pure MH. note the
      // asymmetry: `opts.cache` gates only the WRITE below; this fallback always
      // clears the cache via clearBranding(). that is intentional — losing a
      // brand should always wipe its stale splash cache. the only caller that
      // passes cache:false (the pre-auth login screen) is reached signed-out,
      // when the cache is already empty, so the clear is inert there.
      this.clearBranding();
      return false;
    }

    const root = document.documentElement.style;
    for (const { cssVar, field } of OVERRIDE_VARS) {
      root.setProperty(cssVar, palette[field]);
    }

    // cache the splash fields unless the caller opted out (pre-auth claim path).
    if (opts?.cache !== false) {
      this.writeCache({
        logoUrl: branding?.logoUrl,
        accentColorHex: branding?.accentColorHex,
        brandName: branding?.brandName,
      });
    }
    return true;
  }

  /**
   * Remove every brand override (revert to the MH defaults) and clear the
   * splash cache. Called on sign-out and whenever there is no active branding.
   */
  clearBranding(): void {
    const root = document.documentElement.style;
    for (const { cssVar } of OVERRIDE_VARS) {
      root.removeProperty(cssVar);
    }
    this.clearCache();
  }

  /**
   * Read the cached splash branding written by applyBranding(). Used by the
   * in-app splash to theme warm boots before the user DB loads. Returns null
   * when nothing is cached or the cache is unreadable.
   */
  getCachedBranding(): CachedCoachBranding | null {
    try {
      const raw = localStorage.getItem(COACH_BRANDING_CACHE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as CachedCoachBranding;
    } catch (err: any) {
      // a corrupt cache must not break boot — log it and behave as "no cache".
      this.logging.trackSilentProblem('coach-branding-cache-read-failed', {
        message: err?.message,
      });
      return null;
    }
  }

  private writeCache(cached: CachedCoachBranding): void {
    try {
      localStorage.setItem(COACH_BRANDING_CACHE_KEY, JSON.stringify(cached));
    } catch (err: any) {
      // a failed cache write (quota / private mode) only costs us the warm-boot
      // splash theming — the live theme is already applied. Surface it as a
      // silent problem so a debugger can see it, then continue.
      this.logging.trackSilentProblem('coach-branding-cache-write-failed', {
        message: err?.message,
      });
    }
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(COACH_BRANDING_CACHE_KEY);
    } catch {
      // removeItem effectively never throws; ignore.
    }
  }
}
